/*
 * The full account of the AI suggestions that could not be applied to this
 * note, opened from the review bar's summary affordance.
 *
 * It replaces the list that used to stack inline in the bar, which grew
 * unbounded and pushed the note down. Four situations land here and read
 * identically: a suggestion whose anchor was lost or whose text could not be
 * placed, and a suggestion whose condition is no longer valid — each detected
 * either when the note revealed its staged suggestions or when the clinician
 * clicked Accept.
 *
 * A row leads with the suggestion's own text, because that is the thing the
 * clinician is deciding about and the thing they may want to paste in by hand —
 * hence the copy control. Where it would have gone is real information but
 * secondary, so it sits under the text with the reason.
 *
 * Dismissing persists a reject for that record. Records left alone stay pending,
 * so a later mount can still place them.
 */
import { Button } from '@seen/ui'
import { IconAlertTriangle, IconCircleCheck, IconX } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { CopyButton } from '#src/components/core/CopyButton.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import type { DroppedSuggestion } from '#src/hooks/useAISuggestionHydration.ts'

const RECOVERED_ROW_REASON = 'The note has changed and this can be placed now.'

export function DroppedSuggestionsDialog({
  dropped,
  open,
  onOpenChange,
  onDismiss,
}: {
  dropped: DroppedSuggestion[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Persist a reject for one record. Absent on editors with no staging
   * persistence, which then get a read-only listing.
   */
  onDismiss?: (ids: string[]) => void
}): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* notScrollable: the list owns its own overflow below, so the dialog does
       * not need the route-aware ScrollArea wrapper. */}
      <DialogDisplay notScrollable>
        <div className="flex flex-col overflow-hidden">
          <div
            data-fake-focus-trap="true"
            // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on dialog open so no visible field shows a focus ring; Tab moves on to the real first focusable child
            tabIndex={0}
            className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
          />
          <DialogHeader className="flex-col gap-1 py-4">
            <DialogTitle>
              {`${dropped.length} suggestion${dropped.length === 1 ? '' : 's'} couldn't be placed`}
            </DialogTitle>
            <DialogDescription>
              Nothing was added to the note. These stay staged unless you
              dismiss them, so they can still apply later.
            </DialogDescription>
          </DialogHeader>
          <DialogContent className="min-h-0 flex-1 overflow-y-auto pt-0">
            <ul className="flex flex-col gap-2">
              {dropped.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3 rounded-md border border-border p-3"
                >
                  <div className="flex min-w-0 items-start gap-2">
                    {/* Controls that annotate the row's first line -- this
                     * icon, the copy control, and Dismiss -- each center
                     * inside a one-line-tall box, so they share an axis with
                     * the title whatever font size the row inherits and
                     * whatever height the description below grows to. The box
                     * must be a bare wrapper: `lh` resolves against the
                     * element's OWN font, so putting it on a control that
                     * sets its own text size (every Button variant sets
                     * text-sm) measures that control's line, not the row's. */}
                    <span className="flex h-lh shrink-0 items-center">
                      {item.place ? (
                        <IconCircleCheck className="size-4 text-foreground-secondary" />
                      ) : (
                        <IconAlertTriangle className="size-4 text-foreground-secondary" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-start gap-1">
                        {/* The suggestion's own text leads. With no content of
                         * its own (a checkbox toggle) the placement is all
                         * there is to name it by, so it takes this line
                         * instead of being repeated below. */}
                        <span className="font-medium whitespace-pre-wrap">
                          {item.content ?? item.label}
                        </span>
                        {item.content !== undefined && (
                          <span className="flex h-lh shrink-0 items-center">
                            <CopyButton
                              value={item.content}
                              ariaLabel="Copy suggestion text"
                              className="-m-0.5 size-auto p-0.5"
                            />
                          </span>
                        )}
                      </div>
                      {item.content !== undefined && (
                        <div className="text-sm text-foreground-secondary">
                          {item.label}
                        </div>
                      )}
                      {/* A row whose record places cleanly again states that,
                       * not the reason it failed at reveal — the note has
                       * since changed and that reason is no longer true of
                       * it. The bar's summary control is what places it. */}
                      <div className="text-sm text-foreground-secondary">
                        {item.place ? RECOVERED_ROW_REASON : item.reason}
                      </div>
                    </div>
                  </div>
                  {onDismiss && (
                    <div className="flex h-lh shrink-0 items-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => onDismiss([item.id])}
                      >
                        <IconX className="size-3.5" />
                        Dismiss
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </DialogContent>
          <DialogFooter className="pt-0">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </div>
      </DialogDisplay>
    </Dialog>
  )
}
